import type {Pagination} from '../types'

export const jsonHeaders = {'Content-Type': 'application/json; charset=UTF-8', 'Accept': 'application/json'} as HeadersInit

type Body = object | string | FormData | File

class Api {
  prefix = '/api/v1'

  request(path: string, init?: RequestInit | { body?: Body, headers?: HeadersInit }): Promise<Response> {
    if (!path.startsWith('/')) throw new Error('Invalid path: ' + path)
    path = this.prefix + path

    const body = init?.body
    const requestHeaders = body instanceof FormData ? init?.headers : {...(init?.headers ?? jsonHeaders)}

    return fetch(path, {
      ...init,
      body: body instanceof File || body instanceof FormData || typeof body == 'string' ? body : body && JSON.stringify(body),
      headers: requestHeaders
    }).catch(this.handleFetchFailure) as Promise<Response>
  }

  requestJson(path: string, init?: RequestInit | { body?: Body, headers?: HeadersInit }) {
    return this.request(path, init).then(this.extractJsonHandlingErrors)
  }

  private async extractJsonHandlingErrors(response: Response): Promise<any> {
    let data: any
    try {
      const responseBody = await response.text()
      data = responseBody.trim() ? JSON.parse(responseBody.trim()) : {}
    } catch (e) {
      console.error('Not a JSON', e)
      throw {message: 'notJson'}
    }

    if (response.status < 200 || response.status >= 400) {
      data.message = data.fieldErrors ? Object.values(data.fieldErrors).flatMap(v => v).join('\n') :
        data.detail ?? data.message ?? data.reason
      data.status ??= response.status
      if (data.status == 404) data.message += '\n' + response.url.replace(location.origin, '')
      throw data
    }
    return data
  }

  private handleFetchFailure(error: any) {
    if (error.message === 'Failed to fetch' || error.message === 'Load failed' || error.errno) throw {message: 'networkUnavailable'}
    else throw error
  }

  async get<T>(path: string): Promise<T> {
    return this.requestJson(path)
  }

  async getWithPagination<T>(path: string): Promise<{ pagination: Pagination, items: T }> {
    const response = await this.request(path)
    return {
      items: await this.extractJsonHandlingErrors(response),
      pagination: JSON.parse(response.headers.get('Pagination') || '{}') as Pagination
    }
  }

  post<T>(path: string, body?: Body, headers?: HeadersInit): Promise<T> {
    return this.requestJson(path, {method: 'POST', body, headers})
  }

  put<T>(path: string, body?: Body, headers?: HeadersInit): Promise<T> {
    return this.requestJson(path, {method: 'PUT', body, headers})
  }

  delete(path: string) {
    return this.requestJson(path, {method: 'DELETE'})
  }

  patch<T>(path: string, body: object) {
    return this.requestJson(path, {method: 'PATCH', body})
  }
}

export default new Api()
